import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

interface ListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  advertiserId?: string | number;
  visibility?: string;
}

export function useOffers(params: ListParams = {}) {
  const { page = 1, pageSize = 50, search, status, advertiserId, visibility } = params;

  return useQuery({
    queryKey: ["offers", { page, pageSize, search, status, advertiserId, visibility }],
    queryFn: async () => {
      const qp: Record<string, string> = {
        page: String(page),
        pageSize: String(pageSize),
      };
      if (search) qp.search = search;
      if (status) qp.status = status;
      if (advertiserId) qp.advertiser_id = String(advertiserId);
      if (visibility) qp.visibility = visibility;

      const res = await api.get("/v1/offers", qp);
      if (!res.ok) throw new Error("Failed to fetch offers");
      return res.json();
    },
  });
}

export function useOffer(id: number | string) {
  return useQuery({
    queryKey: ["offers", id],
    queryFn: async () => {
      const res = await api.get(`/v1/offers/${id}`);
      if (!res.ok) throw new Error("Failed to fetch offer");
      return res.json();
    },
    enabled: !!id,
  });
}

export function useCreateOffer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await api.post("/v1/offers", data);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to create offer");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["offers"] });
    },
  });
}

export function useUpdateOffer(id: number | string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await api.patch(`/v1/offers/${id}`, data);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to update offer");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["offers"] });
    },
  });
}

export function useDeleteOffer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number | string) => {
      const res = await api.delete(`/v1/offers/${id}`);
      if (!res.ok) throw new Error("Failed to delete offer");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["offers"] });
    },
  });
}

// --- Offer URLs ---

export function useOfferUrls(offerId: number | string) {
  return useQuery({
    queryKey: ["offers", offerId, "urls"],
    queryFn: async () => {
      const res = await api.get(`/v1/offers/${offerId}/urls`);
      if (!res.ok) throw new Error("Failed to fetch offer URLs");
      return res.json();
    },
    enabled: !!offerId,
  });
}

export function useCreateOfferUrl(offerId: number | string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { name: string; url: string; previewUrl?: string }) => {
      const res = await api.post(`/v1/offers/${offerId}/urls`, data);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to create offer URL");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["offers", offerId, "urls"] });
      queryClient.invalidateQueries({ queryKey: ["offers", offerId] });
      queryClient.invalidateQueries({ queryKey: ["offers", offerId, "tracking-links"] });
    },
  });
}

export function useDeleteOfferUrl(offerId: number | string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (urlId: number) => {
      const res = await api.delete(`/v1/offers/${offerId}/urls/${urlId}`);
      if (!res.ok) throw new Error("Failed to delete offer URL");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["offers", offerId, "urls"] });
      queryClient.invalidateQueries({ queryKey: ["offers", offerId] });
      queryClient.invalidateQueries({ queryKey: ["offers", offerId, "tracking-links"] });
    },
  });
}

// --- Offer Affiliates ---

export function useOfferAffiliates(offerId: number | string) {
  return useQuery({
    queryKey: ["offers", offerId, "affiliates"],
    queryFn: async () => {
      const res = await api.get(`/v1/offers/${offerId}/affiliates`);
      if (!res.ok) throw new Error("Failed to fetch offer affiliates");
      return res.json();
    },
    enabled: !!offerId,
  });
}

export function useAssignAffiliate(offerId: number | string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { affiliateId: number; status?: string; approvalStatus?: string }) => {
      const res = await api.post(`/v1/offers/${offerId}/affiliates`, data);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to assign affiliate");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["offers", offerId, "affiliates"] });
      queryClient.invalidateQueries({ queryKey: ["offers", offerId, "tracking-links"] });
    },
  });
}

export function useUpdateOfferAffiliate(offerId: number | string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ assignmentId, ...data }: { assignmentId: number; status?: string; approvalStatus?: string; payoutOverride?: number | null; revenueOverride?: number | null }) => {
      const res = await api.patch(`/v1/offers/${offerId}/affiliates/${assignmentId}`, data);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to update assignment");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["offers", offerId, "affiliates"] });
      queryClient.invalidateQueries({ queryKey: ["offers", offerId, "tracking-links"] });
    },
  });
}

export function useRemoveOfferAffiliate(offerId: number | string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (assignmentId: number) => {
      const res = await api.delete(`/v1/offers/${offerId}/affiliates/${assignmentId}`);
      if (!res.ok) throw new Error("Failed to remove affiliate");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["offers", offerId, "affiliates"] });
      queryClient.invalidateQueries({ queryKey: ["offers", offerId, "tracking-links"] });
    },
  });
}

// --- Tracking Links ---

export function useTrackingLinks(offerId: number | string) {
  return useQuery({
    queryKey: ["offers", offerId, "tracking-links"],
    queryFn: async () => {
      const res = await api.get(`/v1/offers/${offerId}/tracking-links`);
      if (!res.ok) throw new Error("Failed to fetch tracking links");
      return res.json();
    },
    enabled: !!offerId,
  });
}
